import React, { useRef } from 'react';
import { Typography, Button, Box, Paper, Divider, Grid } from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
// download icon from mui
import GetAppIcon from '@mui/icons-material/GetApp';
import {
    StyledBox,
    StyledTypography,
    StyledFormControl,
    StyledButton,
    StyledMenuItem,
    StyledInput,
    StyledTextField,
    StyledSelect,
  } from "../styles/styles";

const data = {
    certificate_id: '12345',
    issued_date: '2024-08-29',
    valid_until: '2025-08-29',
    battery_name: 'Battery X',
    battery_id: 'BX1234',
    user_name: 'Demo User',
    battery_capacity: '30.6',
    manufacturer: 'Thinkclock',
    model: 'Model X',
    data_source: 'Digital Twin & Upload Data',
    soh_percentage: '98',
    source_of_data: 'Digital Twin',
    voltage_irregularities: 'No irregularities detected.',
    capacity_degradation: 'Capacity degradation is within normal range.',
    resistance_increase: 'Slight increase observed, remains within safe limits.',
    predicted_failure_mode: 'No imminent failure modes detected.',
    cycle_life_estimation: 'Approximately 1500 cycles remaining.',
    additional_observations: 'Performance consistent with expected behavior.',
    thermal_runaway_risk_level: 'Low',
    temperature_threshold_exceedance: 'No exceedance recorded.',
    thermal_hotspots: 'No hotspots detected.',
    safety_recommendations: 'Maintain regular monitoring.',
    estimated_life_remaining: 'Approximately 4 years at current usage rate.',
    degradation_rate: '1% per 100 cycles.',
    cycles_completed: '700 cycles completed.',
    cycles_remaining: '1600 cycles remaining.',
    soh_new: '99',
    soh_current: '98',
    soh_old: '95',
    frequency_response_analysis: 'Stable impedance across frequencies; minor deviations noted at high frequencies.',
    maintenance_recommendations: 'Perform routine inspections every 6 months.',
    operational_guidelines: 'Avoid extreme temperature environments.',
    end_of_life_considerations: 'Plan for replacement within 2 years based on current degradation trends.',
    authorizer_name: 'Babu Devnarayan',
    authorizer_title: 'Battery Analyst',
    signature: 'B.D.',
};

const BatteryCertificate = () => {
    const certificateRef = useRef();

    const generatePDF = async () => {
        const canvas = await html2canvas(certificateRef.current, {
            scale: 3, 
            useCORS: true 
        });
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'px',
            format: [canvas.width, canvas.height], 
        });
        pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
        pdf.save('battery_certificate.pdf');
    };

    return (
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', padding: '20px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h5" sx={{ color: '#e8442d', fontFamily: '"Bai Jamjuree", sans-serif' }}>
                    Battery Health Certificate <VerifiedIcon />
                </Typography>
                <Button variant="contained" color="primary" onClick={generatePDF} sx={{
                    borderColor: "#ff851b",
                    backgroundColor: "#e8442d",
                    color: "white",
                    fontWeight: "900",
                    fontFamily: "Bai Jamjuree",
                    "&:hover": {
                        borderColor: "#ffae68",
                        backgroundColor: "#eb5a46",
                    },
                    className: "gap-2",
                }}>
                    Download PDF <GetAppIcon />
                </Button>
            </Box>
            <div style={{ overflowY: 'scroll', marginBottom: '100px'}}>
                <Paper
                    ref={certificateRef}
                    elevation={6}
                    sx={{
                        padding: 4,
                        borderRadius: 2,
                        maxWidth: '1000px',
                        margin: 'auto',
                        backgroundColor: 'rgb(17, 17 ,17)',
                        border: '1px solid ##1d2838',
                        boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                        fontFamily: '"Bai Jamjuree", sans-serif' 
                    }}
                >
                    <Box textAlign="center" sx={{ mb: 4 }}>
                        <StyledTypography variant="h5" sx={{ fontWeight: 'bold', color: '#e8442d', mb: 2 }}>
                            Battery Health Certificate
                        </StyledTypography>
                        <Divider sx={{ mb: 2 }} />
                        <StyledTypography variant="body1">Issued By: <strong>Thinkclock Battery Labs</strong></StyledTypography>
                        <StyledTypography variant="body1">Certificate ID: <strong>{data.certificate_id}</strong></StyledTypography>
                        <StyledTypography variant="body1">Issued Date: <strong>
                            {new Date().toLocaleDateString()}
                            </strong></StyledTypography>
                        <StyledTypography variant="body1" sx={{ mb: 2 }}>Valid Until: <strong>{data.valid_until}</strong></StyledTypography>
                    </Box>

                    {[
                        {
                            title: 'Battery Information',
                            fields: [
                                { label: 'Battery Name', value: data.battery_name },
                                { label: 'Battery ID', value: data.battery_id },
                                { label: 'User', value: data.user_name },
                                { label: 'Capacity', value: `${data.battery_capacity} Ah` },
                                { label: 'Manufacturer', value: data.manufacturer },
                                { label: 'Model', value: data.model },
                                { label: 'Data Source', value: data.data_source },
                            ],
                        },
                        {
                            title: 'Battery SoH Analysis',
                            fields: [
                                { label: 'State of Health (SoH)', value: `${data.soh_percentage}%` },
                                { label: 'Source of Data', value: data.source_of_data },
                                { label: 'Voltage Irregularities', value: data.voltage_irregularities },
                                { label: 'Capacity Degradation', value: data.capacity_degradation },
                                { label: 'Resistance Increase', value: data.resistance_increase },
                                { label: 'Predicted Failure Mode', value: data.predicted_failure_mode },
                                { label: 'Cycle Life Estimation', value: data.cycle_life_estimation },
                                { label: 'Additional Observations', value: data.additional_observations },
                            ],
                        },
                        {
                            title: 'Thermal Runaway Risk Assessment',
                            fields: [
                                { label: 'Risk Level', value: data.thermal_runaway_risk_level },
                                { label: 'Temperature Threshold Exceedance', value: data.temperature_threshold_exceedance },
                                { label: 'Thermal Hotspots', value: data.thermal_hotspots },
                                { label: 'Safety Recommendations', value: data.safety_recommendations },
                            ],
                        },
                    ].map((section, index) => (
                        <Box key={index} sx={{ mb: 4 }}>
                            <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                                <StyledTypography variant="h6" sx={{ color: '#e8442d', fontWeight: 'bold' }}>
                                    {section.title}
                                </StyledTypography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            <Grid container spacing={2}>
                                {section.fields.map((field, fieldIndex) => (
                                    <Grid item xs={12} sm={6} key={fieldIndex}>
                                        <StyledTypography variant="body1">
                                            <strong>{field.label}:</strong> {field.value}
                                        </StyledTypography>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    ))}

                    <Box sx={{ p: 2, backgroundColor: 'rgb(17, 17 ,17)', borderRadius: 1 }}>
                        <StyledTypography variant="h6" sx={{ mb: 2, color: '#e8442d', fontWeight: 'bold' }}>
                            Authorized Signatory:
                        </StyledTypography>
                        <StyledTypography variant="body1">Name: <strong>{data.authorizer_name}</strong></StyledTypography>
                        <StyledTypography variant="body1">Title: <strong>{data.authorizer_title}</strong></StyledTypography>
                        <StyledTypography variant="body1">Signature: <strong>{data.signature}</strong></StyledTypography>
                    </Box>
                </Paper>
            </div>
        </div>
    );
};

export default BatteryCertificate;
